import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import numeral from 'numeral'
import styled from 'styled-components'

// Hooks
import useRealNode from '../../hooks/useRealNode'
import { useCashoutsPending } from './use-cashouts-pending'

// Components
import Text from '../Text'
import Iconify from '../Iconify'
import Loading from '../Loading'
import { Status } from './Status'
import { CancellTrigger } from './CancellTrigger'
import { BalanceRealAnimated } from './BalanceRealAnimated'
import { Table, TableContainer, Td, Th, Tr } from './CahoutsTable'
import { Content, ModalPanel } from '../ConfirmModal/ConfirmModal'

// Utils
import { publish, subscribe, unsubscribe } from '../../utils/events'

// Styles
import '../Payments/shared/payments.css'

import iconCancel from '../Payments/shared/assets/icono-transferir-saldo.png'

const EVENT_NAME = 'CASHOUTS_PENDING:SHOW_MODAL'

export const showPopupCashoutsPending = ({
  buttonConfirm = 'Continuar con Abono',
  buttonCancel = 'Cancelar Abono'
}) => {
  // Cacheamos el resolve
  return new Promise((resolve) => {
    resolve = resolve
    publish(EVENT_NAME, {
      resolve,
      buttonConfirm,
      buttonCancel
    })
  })
}

export const PopupCashoutsPending = () => {
  const node = useRealNode('modal-node-portal')
  const [show, setShow] = useState(false)
  const resolveRef = useRef(null)
  const [buttonText, setButtonText] = useState({
    confirm: 'Continuar',
    cancel: 'Cancelar'
  })

  // Event Listeners
  useEffect(() => {
    const listener = (data) => {
      const { buttonConfirm, buttonCancel, resolve } = data.detail

      setButtonText({
        confirm: buttonConfirm,
        cancel: buttonCancel
      })

      setShow(true)

      // Cacheamos el resolve de la promesa
      resolveRef.current = resolve
    }

    subscribe(EVENT_NAME, listener)

    return () => {
      unsubscribe(EVENT_NAME, listener)
    }
  }, [])

  const handleClose = (event) => {
    event.stopPropagation()
    event.preventDefault()

    cancelCashouts()
  }
  
  const stopPropagation = (event) => {
    event.stopPropagation()
  }

  const cancelCashouts = () => {
    resolveRef.current('WAS_CANCELLED_PENDING')
    setShow(false)
  }

  const continueWithPayment = () => {
    resolveRef.current('CONTINUE_WITH_PAYMENT')
    setShow(false)
  }

  if (!show) return null

  return ReactDOM.createPortal(
    (
      <ModalPanel tabIndex='1' onMouseDown={stopPropagation}>
        <Content style={{ maxWidth: 550, minHeight: 200 }}>
          <Header>
            {/* <BackButton onClick={handleClose}>
              <Iconify icon='ic:baseline-arrow-back' />
            </BackButton> */}
            <CloseButton onClick={handleClose}>
              <Iconify icon='ic:baseline-close' />
            </CloseButton>
            <IconContainer>
              <img src={iconCancel} alt='Cancelar retiros' />
            </IconContainer>
            <TitleContainer>
              <TitleWhite>Cancelar</TitleWhite>
              <TitleGreen>retiros</TitleGreen>
            </TitleContainer>
          </Header>

          <Panel>
            <Info>
              <WarningIcon>
                <Iconify icon='emojione:warning' style={{ fontSize: '1.3em', color: '#E00034' }} /> 
              </WarningIcon>
              <WarningText>
                Tienes retiros pendientes. Puedes cancelarlos y el monto se reflejará <strong>inmediatamente</strong> en tu saldo.
              </WarningText>
            </Info>

            <TableContent />

            {/* <BalanceRealAnimated /> */}
          
            <Footer>
              <ButtonConfirmGreen onMouseDown={continueWithPayment} className='payment-button'>
                {buttonText.confirm}
              </ButtonConfirmGreen>
            </Footer>
          </Panel>
        </Content>
      </ModalPanel>
    ), node)
}

export const TableContent = () => {
  const { cashouts, isLoading } = useCashoutsPending()

  return (
    <>
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loading />
        </div>
      )}

      {cashouts.length === 0 && !isLoading && (
        <Text center as='p' mt={20} mb={20} color='#9c9c9c'>No hay solicitudes de retiro pendientes</Text>
      )}

      {cashouts.length > 0 && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Fecha</Th>
                <Th>Monto</Th>
                {/* <Th>Cuenta</Th> */}
                {/* <Th>Estado</Th> */}
                <Th>Cancelación</Th>
              </tr>
            </thead>
            <tbody>
              {cashouts.map((cashout, index) => (
                <Tr key={index}>
                  <Td>{new Date(cashout.created).toLocaleDateString()}</Td>
                  <Td>{numeral(cashout.amount / 100).format('$0,000.00')}</Td>
                  {/* <Td>{cashout.bank}...{cashout.bank_code}</Td> */}
                  {/* <Td>
                    <StatusPill status={cashout.status}>
                      {cashout.status === 'pending' || cashout.status === 'processing' ? 'En curso' : cashout.status}
                    </StatusPill>
                  </Td> */}
                  <Td>
                    <CancellTrigger cashout={cashout} closeAfterCancel index={index} />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}


const Panel = styled.div`
  background-color: #191919;
  border: 1px solid #414244;
  border-radius: 0.5em;
  overflow: hidden;
  max-width: 500px;
  margin: 0 auto;
  margin-bottom: 2em;
`

const Header = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 10px;
  background: transparent;
`

const BackButton = styled.button`
  position: absolute;
  left: 20px;
  top: 20px;
  border: 2px solid #ffffff;
  background: transparent;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  
  &:hover {
    opacity: 0.7;
  }
`

const CloseButton = styled.button`
  position: absolute;
  right: 20px;
  top: 20px;
  border: 2px solid #ffffff;
  background: transparent;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  
  &:hover {
    opacity: 0.7;
  }
`

const IconContainer = styled.div`
  width: 48px;
  height: 48px;

  img {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 22px;
  margin-top: 10px;
`

const TitleWhite = styled.span`
  color: #ffffff;
  font-weight: bold;
`

const TitleGreen = styled.span`
  color: #96ff00;
  font-weight: bold;
`

const Info = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-radius: 8px;
  padding: 16px;
  margin: 20px;
  color: #ffffff;
`

const WarningIcon = styled.div`
  color: #ffc400;
  flex-shrink: 0;
  margin-top: 2px;
  background-color: #191919;
  width: 24px;
  height: 24px;
`

const WarningText = styled.p`
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
`

const StatusPill = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  border: 2px solid #96ff00;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
`

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding: 0 20px;
  margin-bottom: 25px;
`

const ButtonCancelRed = styled.button`
  flex: 1;
`

const ButtonConfirmGreen = styled.button`
  flex: 1;
`
